/**
 * Chakra UI Provider Component
 *
 * This component wraps the application with Chakra UI's context providers,
 * enabling theme support, color mode (dark/light), and access to Chakra's
 * styling system throughout the component tree.
 *
 * @fileoverview Chakra UI provider setup with theme and color mode support
 *
 * @example
 * // Usage in main-vite.tsx
 * import { AppChakraProvider } from './providers/ChakraProvider';
 *
 * root.render(
 *   <AppChakraProvider>
 *     <App />
 *   </AppChakraProvider>
 * );
 */

import React, { type ReactNode } from 'react';
import {
  ChakraProvider,
  ColorModeScript,
  type ChakraProviderProps,
} from '@chakra-ui/react';
import { theme } from '../theme';

/**
 * Props interface for AppChakraProvider
 *
 * @property children - Child components to be wrapped by the provider
 * @property resetCSS - Whether to include Chakra's CSS reset (default: true)
 */
interface AppChakraProviderProps {
  /**
   * React children to be rendered within the Chakra context
   */
  children: ReactNode;

  /**
   * Whether to apply Chakra's CSS reset
   * Set to false if you want to preserve existing global styles
   * @default true
   */
  resetCSS?: boolean;

  /**
   * Additional props to pass to ChakraProvider
   */
  chakraProps?: Omit<ChakraProviderProps, 'children' | 'theme' | 'resetCSS'>;
}

/**
 * AppChakraProvider Component
 *
 * A wrapper component that provides Chakra UI context to the application.
 * This includes:
 *
 * 1. **Theme Provider**: Makes the custom theme available to all Chakra components
 * 2. **Color Mode**: Enables dark/light mode switching with localStorage persistence
 * 3. **CSS Reset**: Optionally applies Chakra's CSS reset for consistent styling
 *
 * ## Why We Need This
 *
 * Chakra UI uses React Context to provide theme values and color mode state
 * to all child components. Without this provider:
 * - Chakra components won't have access to theme values
 * - useColorMode and other hooks won't work
 * - Components will fall back to unstyled defaults
 *
 * ## Color Mode Persistence
 *
 * The ColorModeScript is included to:
 * - Read the stored color mode from localStorage before React hydrates
 * - Prevent flash of wrong color mode on page load
 * - Must be placed before the main content renders
 *
 * ## Integration with Existing App
 *
 * This provider is designed to wrap the existing app without affecting
 * current styles. The CSS reset can be disabled if needed by passing
 * resetCSS={false}.
 *
 * @param props - Component props
 * @param props.children - Child components to wrap
 * @param props.resetCSS - Whether to apply CSS reset (default: true)
 * @param props.chakraProps - Additional ChakraProvider props
 *
 * @returns JSX.Element - The wrapped children with Chakra context
 *
 * @example
 * // Basic usage
 * <AppChakraProvider>
 *   <App />
 * </AppChakraProvider>
 *
 * @example
 * // Without CSS reset (preserve existing styles)
 * <AppChakraProvider resetCSS={false}>
 *   <App />
 * </AppChakraProvider>
 */
export const AppChakraProvider: React.FC<AppChakraProviderProps> = ({
  children,
  resetCSS = true,
  chakraProps,
}) => {
  return (
    <>
      {/*
       * ColorModeScript
       *
       * This script runs before React hydrates and sets the correct color mode
       * based on localStorage or system preferences. This prevents a flash of
       * wrong colors when the page first loads.
       *
       * The initialColorMode must match the one defined in theme config.
       * Using 'dark' to match our theme's initialColorMode setting.
       */}
      <ColorModeScript initialColorMode={theme.config.initialColorMode} />

      {/*
       * ChakraProvider
       *
       * The main context provider for Chakra UI. It accepts:
       * - theme: Our custom theme configuration
       * - resetCSS: Whether to apply the CSS reset
       *
       * All Chakra components and hooks must be descendants of this provider.
       */}
      <ChakraProvider
        theme={theme}
        resetCSS={resetCSS}
        {...chakraProps}
      >
        {children}
      </ChakraProvider>
    </>
  );
};

/**
 * Re-export the theme for convenience
 *
 * Allows importing theme from the provider file if needed:
 * import { AppChakraProvider, theme } from './providers/ChakraProvider';
 */
export { theme };

/**
 * Default export for simpler imports
 */
export default AppChakraProvider;
